/**
 * Canonical American odds conversion — single source of truth for the
 * cents→American math used across the trading UI.
 *
 * Background: prior to this module, four separate implementations of the
 * conversion lived in the codebase with diverging conventions at exactly
 * 50¢ (see odds audit 2026-05-25):
 *
 *   - tradeModalBook.americanOdds(50)        → "+100"  (16 live call sites)
 *   - Odds.tsx:centsToAmericanOdds(50)       → -100    (1 live caller: SeriesTable)
 *   - oddsConversion.probabilityToAmerican(0.5) → -100 (marketTransform path)
 *   - marketStore.centsToAmerican(50)        → -100    (writes orphaned field)
 *
 * Both +100 and -100 are conventionally accepted for even money. We pick
 * **+100** here because it matches the dominant trading-surface convention
 * already in use across pills, ladders, tickets, orders, and positions —
 * and matches how DraftKings / FanDuel render even-money lines. The other
 * live converters in this file's wake delegate here.
 *
 * Numerically: at p ∈ (0, 0.5) → positive (underdog), p ∈ (0.5, 1) →
 * negative (favorite). Boundaries (0¢, 100¢) and non-finite inputs return
 * null — American odds are undefined for certain outcomes.
 *
 * IMPORTANT: this module is display-only. It must never feed cents back
 * into order placement — callers must keep the raw cents they had pre-
 * conversion for any wire payload.
 */

/**
 * Convert a probability in cents (0–100) to American odds, rounded to
 * `decimals` places (default 1). Returns null for boundary or non-finite
 * input.
 *
 * The 1-decimal default is load-bearing: a fee-inclusive all-in price like
 * 42.4263¢ must read +135.7, not the integer +136 — the fee detail is the
 * whole point of the line (trader feedback 2026-05-29). Clean integer
 * inputs still render without a trailing `.0` because JS collapses 150.0
 * to 150.
 *
 *   centsToAmericanOdds(40)      →  150    (underdog)
 *   centsToAmericanOdds(50)      →  100    (even money)
 *   centsToAmericanOdds(60)      → -150    (favorite)
 *   centsToAmericanOdds(42.4263) →  135.7  (fee-inclusive all-in price)
 *   centsToAmericanOdds(0)       → null
 *   centsToAmericanOdds(100)     → null
 *
 * IMPORTANT: the rounded value is display-only — never feed it back into
 * order placement (see module header).
 */
export function centsToAmericanOdds(cents: number, decimals = 1): number | null {
  if (!Number.isFinite(cents) || cents <= 0 || cents >= 100) return null;
  const factor = 10 ** decimals;
  const round = (v: number) => Math.round(v * factor) / factor;
  if (cents < 50) return round((100 * (100 - cents)) / cents);
  if (cents > 50) return round((-100 * cents) / (100 - cents));
  return 100; // exactly 50¢ → +100 (even money convention)
}

/**
 * Format a signed American-odds value as a display string with leading sign.
 * Null (boundary or non-finite) renders as the em-dash sentinel.
 *
 * Precision is magnitude-aware by default: 4-digit odds (|odds| ≥ 1000)
 * render as bare integers — a tenth of a point is noise at that magnitude
 * and the decimal just widens the string — while smaller odds keep 1
 * decimal (so a fee-inclusive all-in price reads +135.7, not +136). Because
 * the ladder now left-justifies under the active pill, mixed integer /
 * 1-decimal widths align on their left edge regardless. Pass an explicit
 * `decimals` to force a fixed precision.
 *
 *   formatAmericanOdds( 135.7) → "+135.7"
 *   formatAmericanOdds(-577)   → "-577.0"
 *   formatAmericanOdds( 9900)  → "+9900"   (4-digit → integer)
 *   formatAmericanOdds(-9900)  → "-9900"
 *   formatAmericanOdds( 150, 0)→ "+150"    (explicit override)
 *   formatAmericanOdds(null)   → "—"
 */
export function formatAmericanOdds(odds: number | null, decimals?: number): string {
  if (odds == null) return '—';
  const d = decimals ?? (Math.abs(odds) >= 1000 ? 0 : 1);
  const s = odds.toFixed(d);
  return odds > 0 ? `+${s}` : s;
}

/**
 * Convenience: cents → formatted display string. Composes
 * {@link centsToAmericanOdds} + {@link formatAmericanOdds}. With no explicit
 * `decimals`, rounds to 1 decimal then formats magnitude-aware (integer for
 * 4-digit odds, else 1 decimal).
 */
export function centsToAmericanString(cents: number, decimals?: number): string {
  return formatAmericanOdds(centsToAmericanOdds(cents, decimals ?? 1), decimals);
}

/**
 * Convert a decimal probability (0–1) to American odds. Delegates to
 * {@link centsToAmericanOdds} via × 100. Same +100 convention at p = 0.5.
 */
export function probabilityToAmericanOdds(prob: number, decimals = 1): number | null {
  if (!Number.isFinite(prob)) return null;
  return centsToAmericanOdds(prob * 100, decimals);
}
